/**
 * Work Offers Service - API calls for work offers management
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be';

/**
 * Offer types with colors (moins de types que les demandes)
 */
export const OFFER_TYPES = {
    job_fulltime: { label: 'Plein-temps', color: '#3b82f6' },       // blue
    job_parttime: { label: 'Mi-temps', color: '#60a5fa' },           // blue-light
    job_hours: { label: 'Quelques heures', color: '#06b6d4' },       // cyan
    home_service: { label: 'Service à domicile', color: '#22c55e' }, // green
    project: { label: 'Pour un projet', color: '#8b5cf6' },          // violet
};

/**
 * Urgency badges with colors and sort order
 */
export const URGENCY_BADGES = {
    urgent: { label: 'Urgent', color: '#ef4444', order: 1 },         // red
    this_week: { label: 'Cette semaine', color: '#f97316', order: 2 }, // orange
    asap: { label: 'Dès que possible', color: '#8b5cf6', order: 3 },  // violet
    this_month: { label: 'Ce mois-ci', color: '#eab308', order: 4 },  // yellow
    three_months: { label: 'Dans 3 mois', color: '#84cc16', order: 5 }, // lime
    six_months: { label: 'Dans 6 mois', color: '#06b6d4', order: 6 },  // cyan
    this_year: { label: 'Dans l\'année', color: '#6b7280', order: 7 }, // gray
};

/**
 * Compensation types with colors
 */
export const COMPENSATION_TYPES = {
    paid: { label: 'Rémunéré', color: '#22c55e' },        // green
    exchange: { label: 'Échange', color: '#f59e0b' },     // amber
    volunteer: { label: 'Bénévolat', color: '#ec4899' },  // pink
};

/**
 * Duration types with colors
 */
export const DURATION_TYPES = {
    hour: { label: 'Quelques heures', color: '#60a5fa' },    // blue-light
    days: { label: 'Quelques jours', color: '#3b82f6' },     // blue
    months: { label: 'Quelques mois', color: '#8b5cf6' },    // violet
    year: { label: 'Un an ou plus', color: '#6366f1' },      // indigo
    undefined: { label: 'Indéterminé', color: '#6b7280' },   // gray
};

/**
 * Get all public work offers
 * @param {Object} filters - Filters (type, urgency, search, sortBy, sortDir)
 * @returns {Promise<Array>} List of offers
 */
export const getPublicOffers = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        if (filters.type) params.append('type', filters.type);
        if (filters.urgency) params.append('urgency', filters.urgency);
        if (filters.search) params.append('search', filters.search);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortDir) params.append('sortDir', filters.sortDir);

        const response = await fetch(`${API_BASE}/rest/cabaza/work-offers?${params}`, {
            headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch offers');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const offers = data.data ?? data.DATA ?? [];
        return success ? offers : [];
    } catch (error) {
        console.error('Error fetching public offers:', error);
        return [];
    }
};

/**
 * Get user's own offers
 * @param {number} userId - User ID
 * @returns {Promise<Array>} List of user's offers
 */
export const getMyOffers = async (userId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-offers/my?userId=${userId}`, {
            headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch my offers');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const offers = data.data ?? data.DATA ?? [];
        return success ? offers : [];
    } catch (error) {
        console.error('Error fetching my offers:', error);
        return [];
    }
};

/**
 * Get single offer by ID
 * @param {number} offerId - Offer ID
 * @returns {Promise<Object|null>} Offer data or null
 */
export const getOfferById = async (offerId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-offers/${offerId}`);
        if (!response.ok) throw new Error('Failed to fetch offer');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const offer = data.data ?? data.DATA ?? null;
        return success ? offer : null;
    } catch (error) {
        console.error('Error fetching offer:', error);
        return null;
    }
};

/**
 * Create a new work offer
 * @param {Object} offerData - Offer data
 * @returns {Promise<Object>} Result with success status
 */
export const createOffer = async (offerData) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-offers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(offerData)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE,
            offerId: result.offerId ?? result.OFFERID
        };
    } catch (error) {
        console.error('Error creating offer:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update a work offer
 * @param {number} offerId - Offer ID
 * @param {Object} offerData - Update data
 * @returns {Promise<Object>} Result with success status
 */
export const updateOffer = async (offerId, offerData) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-offers/${offerId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(offerData)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error updating offer:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a work offer
 * @param {number} offerId - Offer ID
 * @param {number} userId - User ID (for ownership verification)
 * @returns {Promise<Object>} Result with success status
 */
export const deleteOffer = async (offerId, userId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-offers/${offerId}?userId=${userId}`, {
            method: 'DELETE'
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error deleting offer:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Close a work offer
 * @param {number} offerId - Offer ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Result with success status
 */
export const closeOffer = async (offerId, userId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-offers/${offerId}/close`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error closing offer:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get type color
 */
export const getTypeColor = (type) => OFFER_TYPES[type]?.color || '#6b7280';

/**
 * Get type label
 */
export const getTypeLabel = (type) => OFFER_TYPES[type]?.label || type;

/**
 * Get urgency color
 */
export const getUrgencyColor = (urgency) => URGENCY_BADGES[urgency]?.color || '#6b7280';

/**
 * Get urgency label
 */
export const getUrgencyLabel = (urgency) => URGENCY_BADGES[urgency]?.label || urgency;

/**
 * Get compensation color
 */
export const getCompensationColor = (compensation) => COMPENSATION_TYPES[compensation]?.color || '#6b7280';

/**
 * Get compensation label
 */
export const getCompensationLabel = (compensation) => COMPENSATION_TYPES[compensation]?.label || compensation;

/**
 * Get duration color
 */
export const getDurationColor = (duration) => DURATION_TYPES[duration]?.color || '#6b7280';

/**
 * Get duration label
 */
export const getDurationLabel = (duration) => DURATION_TYPES[duration]?.label || duration;

/**
 * Get creator profile with photo by peopleUuid
 */
export const getCreatorProfile = async (peopleUuid) => {
    try {
        const profileResponse = await fetch(`${API_BASE}/rest/cabaza/peoples/${peopleUuid}`);
        if (!profileResponse.ok) throw new Error('Failed to fetch profile');
        const profileData = await profileResponse.json();

        let photoUrl = null;
        try {
            const photoResponse = await fetch(`${API_BASE}/rest/cabaza/files/profile-photo/${peopleUuid}`);
            if (photoResponse.ok) {
                const photoData = await photoResponse.json();
                if (photoData.success && photoData.files && photoData.files.length > 0) {
                    photoUrl = photoData.files[0].publicurl || photoData.files[0].publicUrl;
                }
            }
        } catch (photoError) {
            console.log('No profile photo found');
        }

        return {
            success: true,
            profile: profileData.success ? profileData.data : null,
            photoUrl
        };
    } catch (error) {
        console.error('Error fetching creator profile:', error);
        return { success: false, profile: null, photoUrl: null };
    }
};

/**
 * Get occupations for a work offer
 * @param {number} offerId - Offer ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of occupations
 */
export const getOfferOccupations = async (offerId, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-offers/${offerId}/occupations?lang=${lang}`);
        if (!response.ok) throw new Error('Failed to fetch occupations');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const occupations = data.data ?? data.DATA ?? [];
        return success ? occupations : [];
    } catch (error) {
        console.error('Error fetching offer occupations:', error);
        return [];
    }
};

/**
 * Get skills for a work offer
 * @param {number} offerId - Offer ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of skills
 */
export const getOfferSkills = async (offerId, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-offers/${offerId}/skills?lang=${lang}`);
        if (!response.ok) throw new Error('Failed to fetch skills');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const skills = data.data ?? data.DATA ?? [];
        return success ? skills : [];
    } catch (error) {
        console.error('Error fetching offer skills:', error);
        return [];
    }
};
